import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from '../services/api';
import { API, formatError } from '../services/api';
import { motion } from 'framer-motion';
import { Spinner, CheckCircle, Lock, ShieldCheck } from '@phosphor-icons/react';

/*
  Reset Password — public page reached via emailed link ?token=XYZ.
  Validates strength client-side before submit, shows success and signs out
  all sessions (the server bumps token_version as part of the flow).
*/
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const tokenMissing = !token;

  const strength = useMemo(() => {
    const pw = newPw;
    if (!pw) return null;
    const issues = [];
    if (pw.length < 8) issues.push('at least 8 characters');
    if (!/[A-Za-z]/.test(pw)) issues.push('a letter');
    if (!/[0-9\W_]/.test(pw)) issues.push('a number or symbol');
    if (pw.trim() !== pw) issues.push('no leading/trailing spaces');
    return issues;
  }, [newPw]);

  const canSubmit = token && newPw && confirm && strength && strength.length === 0 && newPw === confirm && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (strength && strength.length > 0) {
      setError('Password needs: ' + strength.join(', '));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPw });
      setDone(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (tokenMissing) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex flex-col items-center justify-center px-8 safe-top">
        <div className="w-full max-w-md text-center" data-testid="reset-token-missing">
          <img src="/kindra-logo.png" alt="Kindra" className="h-20 mx-auto object-contain mb-8" />
          <div className="bg-white border border-[#235347]/10 rounded-2xl p-8">
            <h2 className="text-xl font-semibold text-[#0f1a17] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              This reset link is incomplete
            </h2>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              The link is missing its verification token. Please request a new one.
            </p>
            <Link to="/forgot-password" className="inline-block bg-[#235347] text-white rounded-full px-6 py-3 font-medium text-sm hover:bg-[#1c4439] transition-colors">
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex flex-col items-center justify-center px-8 safe-top" data-testid="reset-success">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <img src="/kindra-logo.png" alt="Kindra" className="h-20 mx-auto object-contain mb-8" />
          <div className="bg-white border border-[#235347]/10 rounded-2xl p-8">
            <div className="w-14 h-14 rounded-full bg-[#235347]/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={28} className="text-[#235347]" weight="duotone" />
            </div>
            <h2 className="text-xl font-semibold text-[#0f1a17] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Password updated
            </h2>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              You've been signed out of other devices. Sign in with your new password to continue.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="inline-block bg-[#235347] text-white rounded-full px-6 py-3 font-medium text-sm hover:bg-[#1c4439] transition-colors"
              data-testid="reset-to-login"
            >
              Sign in
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col items-center justify-center px-8 safe-top">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img src="/kindra-logo.png" alt="Kindra" className="h-24 mx-auto object-contain mb-6" />
          <h2 className="text-2xl font-light text-[#0f1a17]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="reset-heading">
            Set a new password
          </h2>
          <p className="text-slate-500 text-sm mt-2 max-w-xs mx-auto leading-relaxed">
            Choose a strong password you don't use anywhere else.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="reset-error">{error}</div>
          )}
          <input
            data-testid="reset-new-password"
            type="password"
            placeholder="New password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all"
            required
            autoFocus
            autoComplete="new-password"
          />
          {strength && strength.length > 0 && (
            <div className="text-xs text-slate-500 ml-1" data-testid="reset-strength-hint">
              Needs: {strength.join(', ')}
            </div>
          )}
          <input
            data-testid="reset-confirm-password"
            type="password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-xl px-5 py-4 focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none transition-all"
            required
            autoComplete="new-password"
          />
          <button
            data-testid="reset-submit"
            type="submit"
            disabled={!canSubmit}
            className="w-full bg-[#235347] text-white rounded-full px-6 py-4 font-medium text-base hover:bg-[#1c4439] transition-all disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
          >
            {loading && <Spinner size={20} className="animate-spin" />}
            Update password
          </button>
          <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5 pt-1">
            <Lock size={12} /> Stored hashed · other devices will be signed out
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
